import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaLink } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { u3125CanModifyInvoice, type U3125InvoiceFormRow, type U3125TaxRateInitItem } from "../../../../U3125Service";
import { NS, U3125ROLES } from "../../../../U3125";
import type { U3134WorkInProgressSearchRow } from "../../../../../u3134WorkInProgress/U3134Service";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import U2000CommonTableFooter from "../../../../../u2000Core/components/tables/basic/U2000CommonTableFooter";
import U3125WipAssociateDlg from "./U3125WipAssociateDlg";
import U3125WipTransactionTable from "../U3125WipTransactionTable";

interface U3125WipLinesTabBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3125InvoiceFormRow>;
  wipCandidates: U3134WorkInProgressSearchRow[];
  wipLoading: boolean;
  taxRates: U3125TaxRateInitItem[];
  onReplaceWipCandidates: (rows: U3134WorkInProgressSearchRow[]) => void;
}

const U3125WipLinesTabBody: React.FC<U3125WipLinesTabBodyProps> = ({
  formStateHandlers,
  wipCandidates,
  wipLoading,
  taxRates,
  onReplaceWipCandidates,
}) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const [associateOpen, setAssociateOpen] = useState(false);

  const data = formStateHandlers.getData();
  const workInProgressLineIds = data?.workInProgressLineIds ?? [];
  const projectId = data?.project?.id ?? "";
  const isNestedEdit =
    formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT && u3125CanModifyInvoice(formStateHandlers.getData()?.status);

  const attachedRows = useMemo(() => {
    const idSet = new Set(workInProgressLineIds);
    return wipCandidates.filter((r) => idSet.has(r.id));
  }, [wipCandidates, workInProgressLineIds]);

  const actions = useMemo(
    () => [
      {
        key: "associate",
        label: t("btnAssociateWip", { ns: NS }),
        handleClick: () => setAssociateOpen(true),
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: !isNestedEdit || !projectId.trim(),
        icon: FaLink,
        Component: U2000ActionBtn,
      },
    ],
    [t, isNestedEdit, projectId, hasRole],
  );

  return (
    <>
      <U3125WipAssociateDlg
        isOpen={associateOpen}
        onClose={() => setAssociateOpen(false)}
        formStateHandlers={formStateHandlers}
        projectId={projectId}
        taxRates={taxRates}
        onApplied={(rows) => {
          onReplaceWipCandidates(rows);
        }}
      />
      <div className="mb-2 flex items-center gap-2">
        <U2000ResponsiveToolbar actions={actions} />
        {wipLoading ? <span className="text-sm text-gray-500">{t("lblLoading", { ns: NS })}</span> : null}
      </div>
      {attachedRows.length === 0 ? (
        <p className="text-sm text-gray-600">{t("msgNoWipLinesAttached", { ns: NS })}</p>
      ) : (
        <U3125WipTransactionTable rows={attachedRows} variant="full" />
      )}
      <U2000CommonTableFooter
        nbItem={attachedRows.length}
        label={t("lblWipLinesGrid", { count: attachedRows.length, ns: NS })}
      />
    </>
  );
};

export default U3125WipLinesTabBody;
